import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAllContentCreatorQuery } from "../../reactWrapper/endpoints/talent/talent.api";
import Table from "../../components/Table/Table";
import Pagination from "../../components/Pagination/Pagination";
import CreatorKPI from "./components/CreatorKPI";
import PageIntro from "../../components/PageIntro/PageIntro";
import LoadingSpinner from "../../components/Loader/LoadingSpinner";

const intentLabels: Record<string, string> = {
  post_jobs: "Post Jobs",
  hire_freelancers: "Hire Freelancers",
  manage_teams: "Manage Teams",
};

const getInitials = (name: string) => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

export default function ContentCreatorDetails() {
  const { id } = useParams() as any;
  const siblingCount = 1;
  const pageColumn = 1;
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState("waitlist");

  const { data, isLoading } = useAllContentCreatorQuery(id ?? "");

  const filteredData = useMemo(() => {
    const list = data?.creatorUsers?.data ?? [];
    return list.filter((item: any) => item.intent_type === activeTab);
  }, [data, activeTab]);

  const totalCount = filteredData.length;

  const tableHeader = useMemo(
    () => [
      { title: "Name", key: "name" },
      { title: "Phone No", key: "phone_no" },
      { title: "Email", key: "email" },
      { title: "Intent", key: "intent" },
      { title: "Company", key: "company_name" },
      { title: "Date", key: "created_at" },
    ],
    []
  );

  const tabHeading = useMemo(
    () => [
      { id: 1, title: "Now", value: "direct" },
      { id: 2, title: "Sales", value: "contact_sales" },
      { id: 3, title: "Soon", value: "waitlist" },
    ],
    []
  );

  const handlePageChange = useCallback(
    (_page: number) => {
      setPage(_page - 1);
    },
    []
  );

  const modifiedData = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end).map((item: any) => [
      <div className="flex gap-3 items-center">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-navy text-white font-semibold text-sm shrink-0">
          {getInitials(item.name ?? "")}
        </div>
        <p className="font-medium">{item.name ?? "—"}</p>
      </div>,
      <p>{item.phone_no ?? "—"}</p>,
      <p>{item.email ?? "—"}</p>,
      <p className="capitalize">{intentLabels[item.intent] ?? item.intent ?? "—"}</p>,
      <p>{item.company_name ?? "—"}</p>,
      <p>{item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}</p>,
    ]);
  }, [filteredData, page, pageSize]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageIntro
        title="Creator Profile"
        description="Leads generated by this micro-content creator."
      />

      <CreatorKPI userId={id} />

      <div className="space-y-4">
        <div className="inline-flex rounded-lg border bg-surface p-1 text-xs">
          {tabHeading.map((each) => (
            <button
              type="button"
              key={each.id}
              onClick={() => {
                setActiveTab(each.value);
                setPage(0);
              }}
              className={`rounded-md px-3 py-1 font-semibold uppercase tracking-wide transition-colors ${
                activeTab === each.value
                  ? "bg-brand-navy text-white"
                  : "text-muted-foreground hover:text-brand-navy"
              }`}
            >
              {each.title}
            </button>
          ))}
        </div>

        <Table
          header={tableHeader}
          label="Converted Clients"
          column={modifiedData}
        />

        <div className="w-full">
            <Pagination
              rangeStart={
                totalCount !== 0
                  ? (page || 0) * ((pageSize || 0) * pageColumn) + 1
                  : 0
              }
              totalCount={totalCount}
              currentPage={(page || 0) + 1}
              pageSize={pageSize}
              pageColumn={pageColumn}
              siblingCount={siblingCount}
              onPageChange={handlePageChange}
            />
        </div>
      </div>
    </div>
  );
}